import { useCallback, useEffect, useRef, useState } from "react";
import {
  API_ROUTES,
  acknowledgeGame2048Win,
  GAME2048_STORAGE_KEY,
  game2048Cells,
  game2048FromGrid,
  game2048MaxTile,
  game2048SaveFromState,
  game2048StateFromSave,
  keyToGame2048Dir,
  LIMITS,
  moveGame2048,
  newGame2048,
  parseGame2048Save,
  pruneGame2048,
  swipeToGame2048Dir,
  type Game2048Dir,
  type Game2048Score,
  type Game2048State,
} from "@ziolo/shared";
import { Button, Card, useFlash } from "@ziolo/ui";
import { ADMIN_LIST, AdminDeleteButton, useAdminListTools } from "../components/admin/AdminChrome.js";
import { Game2048Board } from "../games/game2048/Game2048Board.js";
import { useFormSubmit } from "../hooks/useFormSubmit.js";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { formatDate } from "../lib/format.js";
import { useSession } from "../session/SessionProvider.js";
import "../styles/game2048.css";

interface Boot {
  state: Game2048State;
  best: number;
}

function loadBoot(): Boot {
  try {
    const raw = localStorage.getItem(GAME2048_STORAGE_KEY);
    if (!raw) return { state: newGame2048(), best: 0 };
    const parsed = parseGame2048Save(JSON.parse(raw) as unknown);
    if (parsed.tiles.length === 0) {
      return { state: newGame2048(), best: parsed.best };
    }
    return { state: game2048StateFromSave(parsed), best: parsed.best };
  } catch {
    return { state: newGame2048(), best: 0 };
  }
}

function initialBoot(): Boot {
  const boot = loadBoot();
  if (!import.meta.env.DEV) return boot;
  const flag = new URLSearchParams(window.location.search).get("debug");
  if (flag === "win") {
    return {
      ...boot,
      state: game2048FromGrid([
        [1024, 1024, 2, 4],
        [8, 16, 32, 64],
        [4, 8, 16, 32],
        [2, 4, 8, 16],
      ]),
    };
  }
  if (flag === "lose") {
    const lose = game2048FromGrid([
      [2, 4, 8, 16],
      [32, 64, 128, 256],
      [512, 1024, 2, 4],
      [8, 16, 32, 64],
    ]);
    lose.score = 2048;
    return { ...boot, state: lose };
  }
  return boot;
}

function persist(state: Game2048State, best: number) {
  try {
    localStorage.setItem(
      GAME2048_STORAGE_KEY,
      JSON.stringify(game2048SaveFromState(state, best)),
    );
  } catch {
    // Private mode — the run just doesn't survive a refresh.
  }
}

function blocked(state: Game2048State): boolean {
  return state.over || (state.won && !state.wonAcknowledged);
}

export function Game2048Page() {
  const { user } = useSession();
  const { push } = useFlash();
  const { busy, submit } = useFormSubmit();
  const [boot] = useState(initialBoot);
  const [state, setState] = useState<Game2048State>(boot.state);
  const [best, setBest] = useState(boot.best);
  const [undo, setUndo] = useState<Game2048State | null>(null);
  const [delta, setDelta] = useState(0);
  const [playerName, setPlayerName] = useState("");
  const [savedScore, setSavedScore] = useState(0);
  const scores = useLeaderboard();
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (user && !playerName) setPlayerName(user.username);
  }, [user, playerName]);

  useEffect(() => {
    if (import.meta.env.DEV && new URLSearchParams(window.location.search).get("debug")) return;
    persist(state, best);
  }, [state, best]);

  useEffect(() => {
    const needsPrune = state.tiles.some(
      (tile) => tile.merged || tile.spawned || tile.mergedResult,
    );
    if (!needsPrune) return;
    const id = window.setTimeout(() => {
      setState((current) => pruneGame2048({ ...current, lastMoved: false }));
    }, 180);
    return () => window.clearTimeout(id);
  }, [state]);

  useEffect(() => {
    if (delta <= 0) return;
    const id = window.setTimeout(() => setDelta(0), 700);
    return () => window.clearTimeout(id);
  }, [delta]);

  const play = useCallback((dir: Game2048Dir) => {
    const current = stateRef.current;
    if (blocked(current)) return;
    const next = moveGame2048(current, dir);
    if (!next.lastMoved) return;
    setUndo(pruneGame2048(current));
    setBest((value) => Math.max(value, next.score));
    setDelta(next.score > current.score ? next.score - current.score : 0);
    setState(next);
  }, []);

  const restart = useCallback(() => {
    const next = newGame2048();
    setUndo(null);
    setDelta(0);
    setSavedScore(0);
    setState(next);
  }, []);

  const undoMove = useCallback(() => {
    if (!undo) return;
    setState(undo);
    setUndo(null);
    setDelta(0);
  }, [undo]);

  const continueAfterWin = useCallback(() => {
    setState((current) => acknowledgeGame2048Win(current));
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      const dir = keyToGame2048Dir(event.key);
      if (!dir) return;
      event.preventDefault();
      play(dir);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [play]);

  const showWin = state.won && !state.wonAcknowledged;
  const showLose = state.over && !showWin;
  const canSave = (showWin || showLose) && state.score >= 4 && savedScore < state.score;
  const saved = savedScore > 0 && savedScore >= state.score;

  async function saveScore() {
    const current = stateRef.current;
    const name = (user?.username ?? playerName).trim();
    if (!name) {
      push("Proszę wpisać imię!", "error");
      return;
    }
    const ok = await submit(async () => {
      await apiRequest<Game2048Score>(API_ROUTES.game2048.scores, {
        method: "POST",
        body: {
          name,
          score: current.score,
          maxTile: game2048MaxTile(current),
          cells: game2048Cells(current),
        },
      });
      await scores.reload();
    }, "Wynik zapisany!");
    if (ok) setSavedScore(current.score);
  }

  return (
    <div className="game2048">
      <section className="game2048__play">
        <header className="game2048__header">
          <h2 className="game2048__title">
            <i className="z-icon" aria-hidden="true">
              🔢
            </i>
            2048
          </h2>
          <div className="game2048__scores">
            <div className="game2048-score">
              <span className="game2048-score__label">Wynik</span>
              <strong>{state.score}</strong>
              {delta > 0 ? <span className="game2048-score__delta">+{delta}</span> : null}
            </div>
            <div className="game2048-score">
              <span className="game2048-score__label">Rekord</span>
              <strong>{best}</strong>
            </div>
          </div>
          <div className="game2048__controls">
            <Button variant="primary" onClick={restart}>
              Nowa gra
            </Button>
            <Button disabled={!undo} onClick={undoMove}>
              Cofnij
            </Button>
          </div>
          <p className="game2048__hint">
            Łącz takie same kafelki, aż dojdziesz do 2048. Strzałki, WASD albo przesuń palcem.
          </p>
        </header>

        <Game2048Board
          tiles={state.tiles}
          onSwipe={(dx, dy) => {
            const dir = swipeToGame2048Dir(dx, dy);
            if (dir) play(dir);
          }}
        />
      </section>

      <Game2048Leaderboard scores={scores} />

      {showWin || showLose ? (
        <div
          className="game2048-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="game2048-overlay-title"
        >
          <div className="game2048-overlay__panel">
            <h3 id="game2048-overlay-title">{showWin ? "Wygrałeś!" : "Koniec gry"}</h3>
            <p>
              Wynik: <strong>{state.score}</strong>
              {game2048MaxTile(state) > 0 ? (
                <>
                  {" "}
                  · kafel: <strong>{game2048MaxTile(state)}</strong>
                </>
              ) : null}
            </p>
            {saved ? (
              <p className="hub-hint">Wynik jest już na tablicy.</p>
            ) : canSave ? (
              <label className="game2048-name">
                <span>Imię</span>
                <input
                  className="z-input"
                  value={user ? user.username : playerName}
                  maxLength={LIMITS.game2048Name.max}
                  placeholder="Wpisz swoje imię"
                  readOnly={Boolean(user)}
                  onChange={(event) => {
                    if (!user) setPlayerName(event.target.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") void saveScore();
                  }}
                />
              </label>
            ) : null}
            <div className="game2048-overlay__actions">
              {canSave ? (
                <Button
                  variant="primary"
                  disabled={busy || !(user?.username ?? playerName).trim()}
                  onClick={() => void saveScore()}
                >
                  {busy ? "Zapisywanie..." : "Zapisz wynik"}
                </Button>
              ) : null}
              {showWin ? (
                <Button variant={saved || !canSave ? "primary" : "default"} onClick={continueAfterWin}>
                  Kontynuuj
                </Button>
              ) : null}
              <Button variant={showWin ? "ghost" : saved || !canSave ? "primary" : "default"} onClick={restart}>
                Nowa gra
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Game2048Leaderboard({ scores }: { scores: ReturnType<typeof useLeaderboard> }) {
  const admin = useAdminListTools({
    ...ADMIN_LIST.game2048,
    onReload: scores.reload,
  });

  return (
    <aside className="game2048__ranks">
      <Card variant="panel" title="Najlepsze wyniki" icon="⭐">
        {admin.isAdmin ? (
          <div className="admin-onpage__toolbar">
            <Button size="sm" variant="danger" disabled={admin.busy} onClick={() => void admin.wipe()}>
              Wyczyść tablicę
            </Button>
          </div>
        ) : null}
        {scores.error ? <p className="hub-error">{scores.error}</p> : null}
        {scores.loading ? (
          <p className="hub-hint">Ładowanie...</p>
        ) : scores.items.length === 0 ? (
          <p className="hub-hint">Brak wyników</p>
        ) : (
          <ol className="game2048-leaderboard">
            {scores.items.map((entry, index) => (
              <li
                key={entry.id}
                className={`game2048-leaderboard__row${admin.isAdmin ? " game2048-leaderboard__row--admin" : ""}`}
              >
                <span className="game2048-leaderboard__rank">{index + 1}.</span>
                <span className="game2048-leaderboard__name">{entry.username}</span>
                <span className="game2048-leaderboard__score">{entry.score}</span>
                <span className="game2048-leaderboard__tile">{entry.maxTile}</span>
                <span className="game2048-leaderboard__date">{formatDate(entry.createdAt)}</span>
                {admin.isAdmin ? (
                  <AdminDeleteButton
                    confirm="Usunąć ten wynik?"
                    disabled={admin.busy}
                    onConfirm={() => admin.remove(entry.id)}
                  />
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </Card>
    </aside>
  );
}

function useLeaderboard() {
  const [items, setItems] = useState<Game2048Score[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      const next = await apiRequest<Game2048Score[]>(API_ROUTES.game2048.scores);
      setItems(next);
      setError(null);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { items, error, loading, reload };
}
